import { Component, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { QuestionnaireService } from "../../../services/questionnaire.service";
import {
  FlattenedQuestion,
  QuestionnaireTemplate,
} from "../../../models/questionnaire.model";
import { QuestionnaireMapper } from "../../../utils/questionnaire-mapper";

import ExcelJS from "exceljs";

@Component({
  selector: "app-questionnaire-overview",
  templateUrl: "./questionnaire-overview.component.html",
  styleUrl: "./questionnaire-overview.component.scss",
})
export class QuestionnaireOverviewComponent implements OnInit {
  templateName = "initialQuestionnaire";
  questionnaire: QuestionnaireTemplate | null = null;
  errorMessage: string | null = null;
  isLoadingQuestionnaire = true;
  isExporting = false;

  questions: FlattenedQuestion[] = []; // Stores all questions
  filteredQuestions: FlattenedQuestion[] = [];
  searchQuery = "";
  filterSearch = "";
  activeSubFilter: string | null = null;
  subFilterTopOffset = 0;
  availableFilters = [
    {
      key: "section",
      label: "Section",
      options: [""],
    },
    {
      key: "type",
      label: "Type",
      options: [""],
    },
  ];
  activeFilters: Record<string, string[]> = {
    section: [],
    type: [],
  };
  filters: Record<string, Set<string>> = {
    section: new Set<string>(),
    type: new Set<string>(),
  };
  private readonly questionTypeLabelMap: Record<string, string> = {
    phone_number: "Phone Number",
    multi: "Multi Select",
    single: "Single Select",
    text: "Text",
    textarea: "Long Text",
    number: "Number",
    date: "Date",
    email: "Email",
    checkbox: "Checkbox",
    radio: "Radio",
    dropdown: "Dropdown",
    file: "File Upload",
    boolean: "Yes/No",
  };
  totalQuestions = 0;
  readonly pageSizeOptions = [5, 10, 15, 25, 100, 0];
  private readonly pageSizeStorageKey = "questionnaireOverviewPageSize";
  pageSize = 10; // Number of questions per page
  currentPage = 1;

  paginatedQuestions: FlattenedQuestion[] = [];

  constructor(
    private questionnaireService: QuestionnaireService,
    private route: ActivatedRoute
  ) {}
  ngOnInit(): void {
    this.restorePageSize();
    this.templateName =
      this.route.snapshot.data["templateName"] || this.templateName;
    this.loadQuestionnaire();
  }

  private loadQuestionnaire(): void {
    this.isLoadingQuestionnaire = true;
    this.errorMessage = null;
    this.questionnaireService
      .getQuestionnaireByName(this.templateName)
      .subscribe({
        next: (data) => {
          this.questionnaire = data.template;
          this.questions = QuestionnaireMapper.flattenQuestionnaire(
            data.template
          );
          this.buildFilterOptions();
          this.applyQuestionFilters();
          this.updatePaginatedResponses();
          this.isLoadingQuestionnaire = false;
        },
        error: (err) => {
          console.error("Error loading questionnaire:", err);
          this.errorMessage =
            "An error occurred while loading the questionnaire. Please try again later.";
          this.isLoadingQuestionnaire = false;
        },
      });
  }

  onSearch(term: string) {
    this.searchQuery = term;
    this.currentPage = 1;
    this.applyQuestionFilters();
    this.updatePaginatedResponses();
  }

  getFilteredFilters(): any[] {
    if (!this.filterSearch) return this.availableFilters;
    const lowerSearch = this.filterSearch.toLowerCase();
    return this.availableFilters.filter((filter) =>
      filter.label.toLowerCase().includes(lowerSearch)
    );
  }

  getFilterLabel(key: string): string {
    return this.availableFilters.find((filter) => filter.key === key)?.label || "";
  }

  getSubFilterOptions(key: string): string[] {
    return this.availableFilters.find((filter) => filter.key === key)?.options ?? [];
  }

  getQuestionTypeLabel(type: string): string {
    if (!type) {
      return "";
    }

    return (
      this.questionTypeLabelMap[type] ||
      type
        .split(/[_-]+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    );
  }

  setActiveSubFilter(filterKey: string, elementRef: HTMLElement): void {
    this.activeSubFilter = filterKey;
    const parentTop =
      (
        elementRef.closest(".filter-dropdown-menu") as HTMLElement
      )?.getBoundingClientRect().top ?? 0;
    const itemTop = elementRef.getBoundingClientRect().top;

    this.subFilterTopOffset = itemTop - parentTop + 46;
  }

  toggleFilterOption(filterKey: string, option: string): void {
    const selectedOptions = this.filters[filterKey];
    if (selectedOptions.has(option)) {
      selectedOptions.delete(option);
    } else {
      selectedOptions.add(option);
    }

    this.activeFilters[filterKey] = Array.from(selectedOptions);
  }

  isSelectedFilterOption(filterKey: string, option: string): boolean {
    return this.activeFilters[filterKey]?.includes(option);
  }

  hasSelectedFilterOption(filterKey: string): boolean {
    return (
      Array.isArray(this.activeFilters[filterKey]) &&
      this.activeFilters[filterKey].length > 0
    );
  }

  hasAnyFiltersApplied(): boolean {
    return Object.values(this.activeFilters).some(
      (options) => Array.isArray(options) && options.length > 0
    );
  }

  resetFilter(filterKey: string): void {
    this.filters[filterKey].clear();
    this.activeFilters[filterKey] = [];
  }

  applyFilters(): void {
    this.activeSubFilter = null;
    this.currentPage = 1;
    this.applyQuestionFilters();
    this.updatePaginatedResponses();
  }

  clearFilters(): void {
    Object.keys(this.filters).forEach((key) => {
      this.filters[key].clear();
      this.activeFilters[key] = [];
    });
    this.applyFilters();
  }

  onDropdownClose(isOpen: boolean): void {
    if (!isOpen) {
      this.activeSubFilter = null;
    }
  }

  async exportToExcel() {
    this.isExporting = true;
    try {
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Questions');

      // Define headers
      sheet.columns = [
        { header: 'Section Title', key: 'sectionTitle', width: 20 },
        { header: 'Question Type', key: 'questionType', width: 20 },
        { header: 'Question ID', key: 'questionId', width: 40 },
        { header: 'Question', key: 'question', width: 60 },
        { header: 'Response Options', key: 'responseOptions', width: 40 },
      ];

      // Style header row
      const headerRow = sheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.alignment = { vertical: 'middle' };

      // Helper to stringify response options
      const stringifyOptions = (opts: any): string => {
        if (!opts) return '';
        // If it's already a string, convert comma-separated to line breaks for Excel
        if (typeof opts === 'string') {
          // If it already has line breaks, keep as-is; otherwise split on commas and join with CRLF
          if (opts.includes('\n') || opts.includes('\r')) return opts;
          return opts.split(/\s*,\s*/).join('\r\n');
        }
        // If it's an array of strings
        if (Array.isArray(opts) && opts.every(o => typeof o === 'string')) return opts.join('\r\n');
        // If it's an array of objects with `text`
        if (Array.isArray(opts)) {
          return opts
            .map((o: any) => (typeof o === 'object' && o !== null ? (o.text ?? '') : String(o)))
            .filter(Boolean)
            .join('\r\n');
        }
        // Fallback for unexpected shapes
        try { return JSON.stringify(opts); } catch { return String(opts); }
      };

      // Add all questions (use the full list, not paginated)
      (this.questions || []).forEach(q => {
        sheet.addRow({
          sectionTitle: (q as any).sectionName ?? (q as any).sectionTitle ?? '',
          questionType: (q as any).questionType ?? (q as any).type ?? '',
          questionId: (q as any).questionId ?? '',
          question: (q as any).questionText ?? (q as any).text ?? '',
          responseOptions: stringifyOptions((q as any).responseOptions ?? (q as any).options)
        });
      });

      // Auto-fit row heights for long text (simple wrap)
      sheet.eachRow((row, rowNumber) => {
        row.alignment = { wrapText: true, vertical: 'top' };
      });

      // Generate and download
      const fileName = `${this.templateName || 'questionnaire'}-questions-${new Date().toISOString().slice(0,10)}.xlsx`;
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      // Create a temporary link to trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
      this.isExporting = false;
    } catch (err) {
      this.isExporting = false;
      console.error('Error exporting to Excel:', err);
      this.errorMessage = 'Failed to export. Please try again.';
    }
  }

  onPageChange(page: number) {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.updatePaginatedResponses();
  }

  onPageSizeChange(pageSize: number) {
    this.pageSize = pageSize;
    this.cachePageSize(pageSize);
    this.currentPage = 1;
    this.updatePaginatedResponses();
  }

  get totalPages(): number {
    return Math.ceil(this.totalQuestions / this.effectivePageSize) || 1;
  }

  get effectivePageSize(): number {
    return this.pageSize === 0 ? this.totalQuestions || 1 : this.pageSize;
  }

  private get isMobilePagination(): boolean {
    return typeof window !== "undefined" && window.innerWidth <= 767;
  }

  get paginationItems(): Array<number | string> {
    if (this.isMobilePagination && this.totalPages > 2) {
      return [];
    }

    if (this.totalPages <= 5) {
      return Array.from({ length: this.totalPages }, (_, index) => index + 1);
    }

    if (this.currentPage <= 3) {
      return [1, 2, 3, "ellipsis", this.totalPages];
    }

    if (this.currentPage >= this.totalPages - 2) {
      return [
        1,
        "ellipsis",
        this.totalPages - 2,
        this.totalPages - 1,
        this.totalPages,
      ];
    }

    return [1, "ellipsis", this.currentPage, "ellipsis", this.totalPages];
  }

  get pageRangeStart(): number {
    if (this.totalQuestions === 0) {
      return 0;
    }

    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get pageRangeEnd(): number {
    return Math.min(
      this.currentPage * this.effectivePageSize,
      this.totalQuestions
    );
  }

  private restorePageSize(): void {
    if (typeof window === "undefined") {
      return;
    }

    const cachedPageSize = Number(
      window.localStorage.getItem(this.pageSizeStorageKey)
    );

    if (this.pageSizeOptions.includes(cachedPageSize)) {
      this.pageSize = cachedPageSize;
    }
  }

  private cachePageSize(pageSize: number): void {
    if (typeof window === "undefined") {
      return;
    }

    if (this.pageSizeOptions.includes(pageSize)) {
      window.localStorage.setItem(this.pageSizeStorageKey, String(pageSize));
    }
  }

  private buildFilterOptions(): void {
    const sectionOptions = Array.from(
      new Set(this.questions.map((q) => q.sectionName).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));

    const typeOptions = Array.from(
      new Set(this.questions.map((q) => q.questionType).filter(Boolean))
    ).sort((a, b) =>
      this.getQuestionTypeLabel(a).localeCompare(this.getQuestionTypeLabel(b))
    );

    this.availableFilters = [
      {
        key: "section",
        label: "Section",
        options: sectionOptions,
      },
      {
        key: "type",
        label: "Type",
        options: typeOptions,
      },
    ];
  }

  private applyQuestionFilters(): void {
    const searchTerm = this.searchQuery.trim().toLowerCase();

    this.filteredQuestions = this.questions.filter((question) => {
      const matchesSearch =
        !searchTerm ||
        question.questionText?.toLowerCase().includes(searchTerm) ||
        question.sectionName?.toLowerCase().includes(searchTerm) ||
        question.questionType?.toLowerCase().includes(searchTerm) ||
        this.stringifyResponseOptions(question.responseOptions)
          .toLowerCase()
          .includes(searchTerm);

      const matchesSection =
        this.activeFilters["section"].length === 0 ||
        this.activeFilters["section"].includes(question.sectionName);

      const matchesType =
        this.activeFilters["type"].length === 0 ||
        this.activeFilters["type"].includes(question.questionType);

      return matchesSearch && matchesSection && matchesType;
    });
  }

  private stringifyResponseOptions(responseOptions: unknown): string {
    if (!responseOptions) {
      return "";
    }

    if (Array.isArray(responseOptions)) {
      return responseOptions
        .map((option) =>
          typeof option === "object" && option !== null
            ? JSON.stringify(option)
            : String(option)
        )
        .join(" ");
    }

    return String(responseOptions);
  }

  // Calculate the current page's responses
  updatePaginatedResponses() {
    this.totalQuestions = this.filteredQuestions.length;
    if (this.currentPage > this.totalPages) this.currentPage = this.totalPages;
    if (this.currentPage < 1) this.currentPage = 1;

    const pageSize = this.effectivePageSize;
    const startIndex = (this.currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    this.paginatedQuestions = this.filteredQuestions.slice(startIndex, endIndex);
  }
}
